<template>
  <v-card>
    <v-form @submit.prevent="submitAddAllocation()" ref="addAllocationForm">
      <v-card-title>
        <span class="headline">Add Allocation</span>
      </v-card-title>
      <v-container grid-list-lg>
        <v-layout wrap>
          <v-flex xs12 sm6>
            <v-text-field
              label="Details"
              :color="primaryNeutral"
              v-model="allocation.details"
              :rules="detailsRules"
              prepend-icon="note"
            ></v-text-field>
          </v-flex>
          <v-flex xs12 sm6>
            <v-autocomplete
              label="* Site"
              :color="primaryNeutral"
              :items="sites"
              v-model="allocation.siteId"
              item-text="name"
              item-value="id"
              :loading="loadingSites"
              :rules="siteRules"
              :prepend-icon="$store.state.global.pages.site.icon"
            ></v-autocomplete>
          </v-flex>
          <v-flex xs12 sm6>
            <date-picker-wrapper label="* Date" v-model="allocation.date" :color="primaryNeutral"></date-picker-wrapper>
          </v-flex>
          <v-flex xs12 sm6>
            <v-autocomplete
              label="* Billing Period"
              :color="primaryNeutral"
              prepend-icon="calendar_today"
              :items="openBillingPeriods"
              :rules="billingPeriodRules"
              v-model="allocation.billingPeriod"
            ></v-autocomplete>
          </v-flex>
          <v-flex xs12 sm6>
            <v-autocomplete
              label="Sub-Contractor"
              :color="primaryNeutral"
              :items="contractors"
              v-model="contractorId"
              item-text="name"
              item-value="id"
              :loading="loadingContractors"
              :prepend-icon="$store.state.global.pages.contractor.icon"
            ></v-autocomplete>
          </v-flex>
          <v-flex xs12 sm6>
            <v-autocomplete
              label="Driver"
              :color="primaryNeutral"
              :items="filteredDrivers"
              v-model="allocation.driverId"
              item-text="name"
              item-value="id"
              :loading="loadingDrivers"
              :rules="driverRules"
              :prepend-icon="$store.state.global.pages.driver.icon"
            ></v-autocomplete>
          </v-flex>
          <v-flex xs12 sm6>
            <v-autocomplete
              label="* Vehicle"
              :color="primaryNeutral"
              :items="filteredVehicles"
              v-model="allocation.vehicleId"
              item-text="vehicleName_vehicleType_contractorName"
              item-value="id"
              :loading="loadingVehicles"
              :rules="vehicleRules"
              :prepend-icon="$store.state.global.pages.vehicle.icon"
            ></v-autocomplete>
          </v-flex>
          <v-flex xs12 sm6>
            <v-autocomplete
              label="* Vehicle Type"
              :color="primaryNeutral"
              :items="vehicleTypes"
              v-model="allocation.vehicleTypeId"
              item-text="name"
              item-value="id"
              :loading="loadingVehicleTypes"
              :rules="vehicleTypeRules"
              :prepend-icon="$store.state.global.pages.vehicleType.icon"
            ></v-autocomplete>
          </v-flex>
          <v-flex xs12 sm6>
            <v-autocomplete
              label="* Tip"
              :color="primaryNeutral"
              :items="filteredTips"
              @change="allocation.spoilId = null"
              v-model="allocation.tipId"
              item-text="name"
              item-value="id"
              :loading="loadingTips"
              :rules="tipRules"
              :prepend-icon="$store.state.global.pages.tip.icon"
              clearable
            ></v-autocomplete>
          </v-flex>
          <v-flex xs12 sm6>
            <v-autocomplete
              label="* Spoil"
              :color="primaryNeutral"
              :items="filteredSpoils"
              v-model="allocation.spoilId"
              item-text="name"
              item-value="id"
              :loading="loadingSpoils"
              :rules="spoilRules"
              :prepend-icon="$store.state.global.pages.spoil.icon"
              clearable
            ></v-autocomplete>
          </v-flex>
        </v-layout>
      </v-container>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn :color="primaryGrey" flat @click="close()">Cancel</v-btn>
        <v-btn :color="primaryNeutral" flat type="submit" :loading="addingAllocation">Add</v-btn>
      </v-card-actions>
    </v-form>
  </v-card>
</template>

<script>
import Store from '@/store/computed/storeHelpers'

import FilterDriverVehicles from '@/services/mixins/filterDriverVehicles'
import getDrivers from '@/services/mixins/getMasterFiles/getDrivers'
import getVehicles from '@/services/mixins/getMasterFiles/getVehicles'
import getVehicleTypes from '@/services/mixins/getMasterFiles/getVehicleTypes'
import getTips from '@/services/mixins/getMasterFiles/getTips'
import getSpoils from '@/services/mixins/getMasterFiles/getSpoils'
import getContractors from '@/services/mixins/getMasterFiles/getContractors'
import getSites from '@/services/mixins/getMasterFiles/getSites'

import FilterTipSpoils from '@/services/mixins/filterTipSpoils'
import FormatDates from '@/services/mixins/formatDates'
import BillingPeriods from '@/services/mixins/billingPeriods'

import AllocationService from '@/services/requests/allocationService'
import AllocationValidation from '@/services/validation/allocationValidation'

import DatePickerWrapper from '@/components/ui/datePickerWrapper'
export default {
  mixins: [
    FilterDriverVehicles,
    AllocationValidation,
    getDrivers,
    getVehicles,
    getVehicleTypes,
    getContractors,
    getSpoils,
    getTips,
    getSites,
    FilterTipSpoils,
    FormatDates,
    BillingPeriods,
  ],
  components: {
    DatePickerWrapper,
  },
  data() {
    return {
      prefix: 'allocation',
      allocation: {
        date: '',
        billingPeriod: this.makeBillingPeriod(new Date()),
        driverId: null,
        vehicleId: null,
        vehicleTypeId: null,
        spoilId: null,
        tipId: null,
        siteId: null,
        estimatedSpoilAmount: null,
      },
      addingAllocation: false,
    }
  },
  methods: {
    submitAddAllocation() {
      if (!this.$refs.addAllocationForm.validate()) {
        this.snack({ text: 'Please correct form errors', color: 'red' })
        return
      }
      this.addAllocation()
    },
    addAllocation() {
      this.addingAllocation = true

      AllocationService.addAllocation(this.allocation)
        .then((result) => {
          this.addingAllocation = false
          //console.log(result);
          this.$store.commit('allocations/addToArrayState', {
            prop: 'allocations',
            value: result.data.result,
          })
          this.resetForm()
          this.snack({ color: 'green', text: 'Allocation was added' })
          this.close()
        })
        .catch((err) => {
          this.snackError()
          this.addingAllocation = false
          console.log(err)
        })
    },
    resetForm() {
      Object.keys(this.allocation).forEach((key) => {
        this.allocation[key] = null
      })
      this.$refs.addAllocationForm.resetValidation()
    },
    close() {
      this.$emit('close')
    },
  },
}
</script>

<style>
</style>
